import { styled } from 'opensolar-ui'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { FileRecord, ResourcePrefix, UploadStatus, useFileUploadContext } from '../controller/UploadFileContext'
import { formatFileRecordSize, isUploadDisabled } from '../utils'
import { UploadFileItemBottomProgress } from './UploadFileItemBottomProgress'
import { UploadFileThumbnail } from './UploadFileThumbnail'

const ListAndDropzoneWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: compact ? 6 : 12,
  width: '100%',
}))

const SelectedFilesList = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: compact ? 4 : 12,
}))

const SelectedFileContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  gap: compact ? 8 : 10,
  alignItems: 'center',
  borderRadius: compact ? 0 : 5,
  padding: compact ? 0 : 12,
  border: compact ? 'none' : '2px solid',
  borderColor: theme.palette.grey[300],
  '& p': {
    fontWeight: 500,
    margin: 0,
    flex: 1,
  },
  '& span': {
    color: theme.palette.grey[700],
    fontWeight: 400,
    fontSize: 12,
  },
}))

const FileName = styled('p')(({ theme }) => ({
  fontWeight: 500,
  margin: 0,
  flex: 1,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}))

const FileThumbnailWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  width: compact ? 18 : 32,
  height: compact ? 18 : 32,
  flexShrink: 0,
  borderRadius: compact ? 0 : 5,
  overflow: 'hidden',
}))

type UploadFileListProps = {
  slot: string
  resource_prefix: ResourcePrefix
  uploadStatus: UploadStatus
  onFileRemove?: (record: FileRecord) => void
  dropzone?: React.ReactNode
  compact?: boolean
  showRemoveButton?: boolean
}

export const UploadFileList = ({
  slot,
  resource_prefix,
  uploadStatus,
  onFileRemove,
  dropzone,
  compact = false,
  showRemoveButton = true,
}: UploadFileListProps) => {
  const translate = useTranslate()
  const { fileRecords, setFileRecords } = useFileUploadContext()

  // Get file records for this slot directly from context
  const slotFileRecords = fileRecords.filter((record) => record.slot === slot)

  const removeFile = useCallback(
    (record: FileRecord) => {
      // Remove this record from the context
      const updatedSourceRecords = slotFileRecords.filter((r) => r !== record)
      setFileRecords(slot, updatedSourceRecords, resource_prefix)

      // Call optional callback
      onFileRemove?.(record)
    },
    [slot, resource_prefix, slotFileRecords, setFileRecords, onFileRemove]
  )

  const isDisabled = isUploadDisabled(uploadStatus)

  return (
    <ListAndDropzoneWrapper compact={compact}>
      {dropzone}
      {slotFileRecords.length > 0 && (
        <SelectedFilesList compact={compact}>
          {/* Display all file records */}
          {slotFileRecords.map((record, index) => (
            <SelectedFileContainer
              key={`${record.status}-${record.name}-${index}`}
              compact={compact}
              className="selected-file-container"
            >
              <FileThumbnailWrapper compact={compact}>
                <UploadFileThumbnail record={record} />
              </FileThumbnailWrapper>
              <FileName title={record.name}>{record.name}</FileName>
              {!compact && <span>{formatFileRecordSize(record, translate)}</span>}
              {showRemoveButton && (
                <button
                  disabled={isDisabled}
                  type="button"
                  onClick={() => removeFile(record)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    fontSize: 18,
                    color: '#666',
                  }}
                >
                  ×
                </button>
              )}
              <UploadFileItemBottomProgress fileRecord={record} />
            </SelectedFileContainer>
          ))}
        </SelectedFilesList>
      )}
    </ListAndDropzoneWrapper>
  )
}
